import { useState, useEffect, Suspense, lazy } from 'react';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
const Calendar = lazy(() => import('./components/Calendar'));
import AuthModal from './components/auth/AuthModal';
import AuthCallback from './components/auth/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import Layout from './components/Layout';
import { SUBSCRIPTION_FEATURES } from './config/subscriptionFeatures';
import PageSkeleton from './components/ui/PageSkeleton';
import { ErrorBoundary, RouteErrorBoundary } from './components/ui/ErrorBoundary';
import NotFoundPage from './components/NotFoundPage';
import { useAnalyticsContext } from './hooks/useAnalytics';
import { useUIStore } from './store/uiStore';
import { trackEvent } from './services/analytics';

// Lazy load route-level pages
const LandingPage = lazy(() => import('./components/LandingPage'));
const PricingPage = lazy(() => import('./components/PricingPage'));
const SuccessPage = lazy(() => import('./components/checkout/SuccessPage'));
const TermsPage = lazy(() => import('./components/TermsPage'));
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));
const ContactPage = lazy(() => import('./components/ContactPage'));
const ProfilePage = lazy(() => import('./components/profile/ProfilePage'));
const FinancialImpactCalculator = lazy(() => import('./components/FinancialImpactCalculator'));
const CustodyAnalysis = lazy(() => import('./components/CustodyAnalysis'));
const SavedSchedules = lazy(() => import('./components/SavedSchedules'));
const ScenarioCompare = lazy(() => import('./components/ScenarioCompare'));
const CourtPacketExport = lazy(() => import('./components/CourtPacketExport'));
const AttorneyShareMode = lazy(() => import('./components/AttorneyShareMode'));
const ActualTimeTracker = lazy(() => import('./components/ActualTimeTracker'));
const PlannedVsActualReport = lazy(() => import('./components/PlannedVsActualReport'));
const EvidenceReport = lazy(() => import('./components/EvidenceReport'));
const ChildSupportCalculatorIndex = lazy(() => import('./components/calculators/ChildSupportCalculatorIndex'));
const StateChildSupportCalculator = lazy(() => import('./components/calculators/StateChildSupportCalculator'));
const ResetPassword = lazy(() => import('./components/auth/ResetPassword'));
const WizardPage = lazy(() => import('./components/wizard/WizardPage'));

interface ToastData {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

interface AppContentProps {
  toastData: ToastData | null;
}

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    trackEvent('page_viewed', {
      path: location.pathname,
      search: location.search || null,
    });
  }, [location.pathname, location.search]);

  return null;
}

function AppContent({ toastData }: AppContentProps) {
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const activeModal = useUIStore((s) => s.activeModal);
  const closeModal = useUIStore((s) => s.closeModal);

  // Bridge uiStore modal signals to local auth modal state
  useEffect(() => {
    if (activeModal === 'auth-signin' || activeModal === 'auth-signup') {
      setAuthMode(activeModal === 'auth-signup' ? 'signup' : 'signin');
      setShowAuthModal(true);
      closeModal(); // clear store so it can be triggered again
    }
  }, [activeModal, closeModal]);

  // Keep analytics common dimensions in sync with app state
  useAnalyticsContext();
  
  const handleGetStarted = () => {
    if (user) {
      window.location.href = '/app';
    } else {
      // For non-logged in users, take them directly to the app without requiring signup
      window.location.href = '/app';
    }
  };

  const openSignIn = () => {
    setAuthMode('signin');
    setShowAuthModal(true);
  };

  const openSignUp = () => {
    setAuthMode('signup');
    setShowAuthModal(true);
  };

  return (
    <Layout openSignIn={openSignIn} openSignUp={openSignUp} toastData={toastData}>
      <PageViewTracker />
      <Routes>
        {/* Public routes */}
        <Route 
          path="/" 
          element={user ? <Navigate to="/app" /> : <LandingPage onGetStarted={handleGetStarted} onSignUp={openSignUp} />} 
        />
        <Route path="/pricing" element={
          <RouteErrorBoundary>
            <PricingPage />
          </RouteErrorBoundary>
        } />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/success" element={<SuccessPage />} />
        {/* Password recovery landing page (linked from the reset email) */}
        <Route path="/reset-password" element={
          <Suspense fallback={<PageSkeleton />}>
            <ResetPassword />
          </Suspense>
        } />
        
        {/* App routes - no auth required for basic functionality */}
        <Route path="/app" element={
          <RouteErrorBoundary>
            <Suspense fallback={<PageSkeleton />}><Calendar /></Suspense>
          </RouteErrorBoundary>
        } />

        {/* Full-page schedule wizard */}
        <Route path="/wizard" element={
          <RouteErrorBoundary>
            <Suspense fallback={<PageSkeleton />}><WizardPage /></Suspense>
          </RouteErrorBoundary>
        } />
        
        {/* Auth required routes */}
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute requireAuth={true}>
              <ProfilePage />
            </ProtectedRoute>
          } 
        />
        
        {/* Free tools */}
        <Route
          path="/financial-calculator"
          element={
            <RouteErrorBoundary>
              <Suspense fallback={<PageSkeleton />}>
                <FinancialImpactCalculator />
              </Suspense>
            </RouteErrorBoundary>
          }
        />

        <Route
          path="/custody-analysis"
          element={
            <RouteErrorBoundary>
              <Suspense fallback={<PageSkeleton />}>
                <CustodyAnalysis />
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        
        {/* Scenario Comparison - premium feature */}
        <Route
          path="/scenario-compare"
          element={
            <ProtectedRoute
              requireAuth={true}
              requireSubscription={true}
              requiredFeatures={[SUBSCRIPTION_FEATURES.SCENARIO_COMPARE.id]}
            >
              <ScenarioCompare />
            </ProtectedRoute>
          }
        />

        {/* Court Packet Export - premium feature */}
        <Route
          path="/court-packet"
          element={
            <ProtectedRoute
              requireAuth={true}
              requireSubscription={true}
              requiredFeatures={[SUBSCRIPTION_FEATURES.LEGAL_TEMPLATES.id]}
            >
              <CourtPacketExport />
            </ProtectedRoute>
          }
        />

        {/* Attorney Share Mode - premium feature */}
        <Route
          path="/attorney-share"
          element={
            <ProtectedRoute
              requireAuth={true}
              requireSubscription={true}
              requiredFeatures={[SUBSCRIPTION_FEATURES.LEGAL_TEMPLATES.id]}
            >
              <AttorneyShareMode />
            </ProtectedRoute>
          }
        />

        {/* Actual Time Tracking - standard+ feature */}
        <Route
          path="/actual-time"
          element={
            <ProtectedRoute
              requireAuth={true}
              requireSubscription={true}
              requiredFeatures={[SUBSCRIPTION_FEATURES.TIME_CALCULATOR.id]}
            >
              <ActualTimeTracker />
            </ProtectedRoute>
          }
        />
        {/* Evidence Report - premium feature */}
        <Route
          path="/evidence-report"
          element={
            <ProtectedRoute
              requireAuth={true}
              requireSubscription={true}
              requiredFeatures={[SUBSCRIPTION_FEATURES.EVIDENCE_REPORT.id]}
            >
              <EvidenceReport />
            </ProtectedRoute>
          }
        />

        <Route
          path="/planned-vs-actual"
          element={
            <ProtectedRoute
              requireAuth={true}
              requireSubscription={true}
              requiredFeatures={[SUBSCRIPTION_FEATURES.TIME_CALCULATOR.id]}
            >
              <PlannedVsActualReport />
            </ProtectedRoute>
          }
        />

        {/* Child Support Calculator - state pages (free, SEO) */}
        <Route
          path="/child-support-calculator"
          element={
            <RouteErrorBoundary>
              <Suspense fallback={<PageSkeleton />}>
                <ChildSupportCalculatorIndex />
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/child-support-calculator/:stateSlug"
          element={
            <RouteErrorBoundary>
              <Suspense fallback={<PageSkeleton />}>
                <StateChildSupportCalculator />
              </Suspense>
            </RouteErrorBoundary>
          }
        />

        {/* Saved Schedules - requires an account (schedules are stored per user) */}
        <Route
          path="/saved-schedules"
          element={
            <ProtectedRoute requireAuth={true}>
              <Suspense fallback={<PageSkeleton />}>
                <SavedSchedules />
              </Suspense>
            </ProtectedRoute>
          }
        />
        
        {/* Fallback route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      
      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          defaultMode={authMode}
        />
      )}
    </Layout>
  );
}

function App() {
  const [toastData, setToastData] = useState<ToastData | null>(null);

  // Toast function available for future use
  const _showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToastData({ message, type });
    setTimeout(() => setToastData(null), 3000);
  };
  void _showToast; // Suppress unused warning - available for future use

  return (
    <ErrorBoundary>
    <PWAInstallPrompt />
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <SubscriptionProvider>
          <Suspense fallback={<PageSkeleton />}>
            <Routes>
              {/* Auth callback must be outside Layout so the wildcard doesn't catch it first */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/*" element={<AppContent toastData={toastData} />} />
            </Routes>
          </Suspense>
        </SubscriptionProvider>
      </AuthProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
